// frontend/src/components/Leave/LeaveApplicationDetailModal.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  X,
  XCircle,
  User,
  Building,
  Briefcase,
  Clock,
  CheckCircle,
  AlertTriangle,
  Shield,
  MapPin,
  ChevronRight,
  TrendingUp,
  Activity,
  Zap,
  Award,
  Ban,
  Plane,
  Upload,
  CalendarRange,
  Eye,
  GraduationCap,
  Loader2,
} from 'lucide-react';
import { staffAPI } from '../../services/api';
import { formatDate, formatDateTime, formatDateForInput } from '../../utils/dateUtils';
import FlightTicketVerificationModal from '../FlightTicketVerificationModal';
import { formatLeaveDays, pluralizeDays } from '../../utils/numberUtils';
import {
  formatOilCreditDaysLabel,
  getDisplayTotalDays,
  getOilCreditDaysClassName,
  getOilCreditedDays,
} from '../../utils/leaveDisplayUtils';
import {
  isOffInLieuClaimName,
  isOffInLieuUsageName,
  isNSLeaveName,
  OIL_EXPIRY_DAYS,
} from '../../constants/leaveTypes';
import LoadingSpinner from '../UI/LoadingSpinner';
import Modal from '../UI/Modal';
import { StatusBadge } from '../UI/StatusBadge';
import { ClassificationBadge } from '../UI/ClassificationBadge';
import { InfoBadge } from '../UI/InfoBadge';
import { LeaveTypeBadge } from '../UI/LeaveTypeBadge';
import { TradeBadge } from '../UI/TradeBadge';
import { isTradeRedundantWithDepartment } from '../../config/tradeConfig';
import ConsumptionBreakdown from './ConsumptionBreakdown';
import HomeLeaveConsumptionNote from './HomeLeaveConsumptionNote';
import LeavePeriodDisplay from './LeavePeriodDisplay';
import LeaveExcludedDaysCallout from './LeaveExcludedDaysCallout';
import OilClaimWorkDates from './OilClaimWorkDates';
import { OverseasTravelDeclarationSummary } from './OverseasTravelDeclarationFields';
import toast from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';
import DatePickerField from '../UI/DatePickerField';
import LeaveHalfDaySegmentedControl from './LeaveHalfDaySegmentedControl';
import ApprovalTimeline from './ApprovalTimeline';

/** Parse YYYY-MM-DD to local calendar Date (no UTC shift). */
function parseLocalYmd(value) {
  if (!value) return null;
  const s = String(value).slice(0, 10);
  const parts = s.split('-').map((p) => parseInt(p, 10));
  if (parts.length !== 3 || parts.some((n) => Number.isNaN(n))) return null;
  const [y, m, d] = parts;
  return new Date(y, m - 1, d);
}

const PHASE1_WORK_ARRANGEMENT_TYPES = ['external activity', 'on course', 'work from home'];

/**
 * Resolves course name and location from either dedicated DB fields or by
 * parsing the legacy composite leave_reason string ("Course: X (Y)").
 */
const getCourseInfo = (app) => {
  if (app.course_name || app.course_location) {
    return { courseName: app.course_name, courseLocation: app.course_location };
  }
  if (!app.leave_reason) return null;
  const withLocation = app.leave_reason.match(/^Course:\s*(.+)\s+\(([^)]+)\)$/);
  if (withLocation) return { courseName: withLocation[1].trim(), courseLocation: withLocation[2].trim() };
  const nameOnly = app.leave_reason.match(/^Course:\s*(.+)$/);
  if (nameOnly) return { courseName: nameOnly[1].trim(), courseLocation: null };
  return null;
};
const HR_CORRECTABLE_STATUSES = ['submitted', 'pending', 'verified', 'approved', 'auto_approved'];

function formatApiError(err) {
  const raw = err?.response?.data?.error;
  if (raw == null) return err?.message || 'Request failed.';
  if (typeof raw === 'string') return raw;
  if (Array.isArray(raw)) return raw.map(String).join(' ');
  if (typeof raw === 'object') {
    return Object.entries(raw)
      .flatMap(([k, v]) => {
        if (Array.isArray(v)) return v.map((x) => `${k}: ${x}`);
        return [`${k}: ${v}`];
      })
      .join(' ');
  }
  return String(raw);
}

/**
 * Admin-grade Leave Application Detail Modal.
 *
 * Fetches enriched data from /leave-applications/:id/admin_detail/
 * and renders structured sections for HR decision-making:
 *   1. Employee Profile
 *   2. Leave Details + Balance Snapshot
 *   3. Reason / Context (adaptive per leave type)
 *   4. Coverage Arrangements
 *   5. Compliance Checks
 *   6. Approval Timeline
 *   7. Government Reimbursement (if eligible)
 *   8. Related Applications (parent/child)
 *
 * Props:
 *   isOpen        - boolean
 *   onClose       - () => void
 *   applicationId - number|string  (leave application PK)
 *   onApprove     - async (id) => boolean|void  (optional; return false to keep modal open)
 *   isApproving   - boolean (optional; disables Approve while mutation in flight)
 *   onReject      - (id) => void   (optional, for action buttons)
 *   onCancel      - (id) => void   (optional, admin cancel action)
 *   onRefresh     - () => void     (optional, refresh list on close)
 */
const LeaveApplicationDetailModal = ({
  isOpen,
  onClose,
  applicationId,
  onApprove,
  isApproving = false,
  onReject,
  onCancel,
  onRefresh,
  extraFooterActions,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showFlightVerifyModal, setShowFlightVerifyModal] = useState(false);

  const { canAccessAdmin } = useAuth();
  const [hrCorrectionOpen, setHrCorrectionOpen] = useState(false);
  const [hrStartDate, setHrStartDate] = useState(null);
  const [hrEndDate, setHrEndDate] = useState(null);
  const [hrStartPeriod, setHrStartPeriod] = useState('AM');
  const [hrEndPeriod, setHrEndPeriod] = useState('PM');
  const [hrReason, setHrReason] = useState('');
  const [hrPreviewDays, setHrPreviewDays] = useState(null);
  const [hrPreviewExcludedDates, setHrPreviewExcludedDates] = useState([]);
  const [hrPreviewLoading, setHrPreviewLoading] = useState(false);
  const [hrSubmitting, setHrSubmitting] = useState(false);
  const [hrFormError, setHrFormError] = useState(null);

  const fetchDetail = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await staffAPI.getLeaveApplicationAdminDetail(applicationId);
      setData(res.data);
    } catch (e) {
      console.error('Failed to fetch admin detail:', e);
      setError('Failed to load application details.');
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => {
    if (isOpen && applicationId) {
      fetchDetail();
    }
    if (!isOpen) {
      setData(null);
      setError(null);
      setHrCorrectionOpen(false);
      setHrFormError(null);
    }
  }, [isOpen, applicationId, fetchDetail]);

  // Reset HR correction form when detail payload changes (admin DS: predictable form state)
  useEffect(() => {
    if (!data || !isOpen) return;
    setHrStartDate(parseLocalYmd(data.leave_start_date));
    setHrEndDate(parseLocalYmd(data.leave_end_date));
    setHrStartPeriod(data.leave_start_time_period || 'AM');
    setHrEndPeriod(data.leave_end_time_period || 'PM');
    setHrReason('');
    setHrFormError(null);
    setHrPreviewExcludedDates([]);
    setHrCorrectionOpen(false);
  }, [data, applicationId, isOpen]);

  // Preview working duration (aligns with server: schedule + half-day adjustments)
  useEffect(() => {
    if (!hrCorrectionOpen || !data) return;
    const staffPk = data.leave_staff_id;
    if (!staffPk || !hrStartDate || !hrEndDate) return;
    const startStr = formatDateForInput(hrStartDate);
    const endStr = formatDateForInput(hrEndDate);
    if (!startStr || !endStr) return;

    let cancelled = false;
    setHrPreviewLoading(true);
    const timer = setTimeout(async () => {
      try {
        const res = await staffAPI.calculateLeaveDays({
          start_date: startStr,
          end_date: endStr,
          staff_id: staffPk,
          leave_type_name: (
            data.leave_type?.leave_type_name
            || data.leave_type_name
            || 'Annual Leave'
          ),
        });
        if (cancelled) return;
        let w = parseFloat(res.data?.calculation?.working_days_only ?? 0);
        const startIsPhysicalHalf = res.data?.calculation?.start_is_physical_half_day;
        const endIsPhysicalHalf = res.data?.calculation?.end_is_physical_half_day;
        // Same trim as SmartLeaveApplicationModal: AM/PM only on a physically
        // full day. Saturday on a 5.5-day week is already the whole shift.
        if (hrStartPeriod === 'PM' && !startIsPhysicalHalf) w -= 0.5;
        if (hrEndPeriod === 'AM' && !endIsPhysicalHalf) w -= 0.5;
        const rounded = Math.max(0, Math.round(w * 2) / 2);
        setHrPreviewDays(rounded);
        setHrPreviewExcludedDates(res.data?.calculation?.excluded_dates || []);
      } catch {
        if (!cancelled) {
          setHrPreviewDays(null);
          setHrPreviewExcludedDates([]);
        }
      } finally {
        if (!cancelled) setHrPreviewLoading(false);
      }
    }, 320);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [hrCorrectionOpen, data, hrStartDate, hrEndDate, hrStartPeriod, hrEndPeriod]);

  if (!isOpen) return null;

  // ----- Derived values -----
  const app = data || {};
  const profile = app.employee_profile || {};
  const balance = app.balance_snapshot || null;
  const relatedApps = app.related_applications || [];
  const parentApp = app.parent_application || null;
  const publicHolidays = app.public_holidays_in_period || [];
  const consumptionProposal = app.leave_consumption_proposal || null;
  const oilBalance = app.oil_balance_snapshot || null;
  const overseasLeave = app.overseas_leave_snapshot || null;

  const leaveTypeName = (
    app.leave_type?.leave_type_name ||
    app.leave_type_name ||
    ''
  ).trim();

  const hasOilConsumption = consumptionProposal?.items?.some(
    i => i.leave_type === 'Off in Lieu' && parseFloat(i.days) > 0
  );
  const showConsumptionChips = consumptionProposal?.items?.length > 1 || (
    consumptionProposal?.items?.length === 1 &&
    consumptionProposal.items[0].leave_type !== leaveTypeName
  );

  // Display name: show "Off in Lieu" when AL is 100% OIL consumed, otherwise keep applied type.
  // HL/UGL parents always show their own type — the AL+UL/HL split is shown in breakdown chips.
  const displayLeaveTypeName = (() => {
    const ltLower = leaveTypeName.toLowerCase();
    if (ltLower === 'home leave' || ltLower === 'urgent leave') return leaveTypeName;
    if (consumptionProposal?.items?.length === 1 && consumptionProposal.items[0].leave_type !== leaveTypeName) {
      return consumptionProposal.items[0].leave_type;
    }
    return leaveTypeName;
  })();

  const isOILClaim = isOffInLieuClaimName(leaveTypeName);
  const isOILUsage = isOffInLieuUsageName(leaveTypeName);
  const isNS = isNSLeaveName(leaveTypeName);
  const isWFH = leaveTypeName.toLowerCase() === 'work from home';
  const isExternalActivity = leaveTypeName.toLowerCase() === 'external activity';
  const isOnCourse = leaveTypeName.toLowerCase() === 'on course';
  const isHomeLeave = leaveTypeName.toLowerCase() === 'home leave';
  const isUrgentLeave = leaveTypeName.toLowerCase() === 'urgent leave';
  const isCompassionate = leaveTypeName.toLowerCase().startsWith('compassionate leave');
  const isSickLeave =
    (leaveTypeName.toLowerCase().includes('outpatient') &&
      leaveTypeName.toLowerCase().includes('sick')) ||
    leaveTypeName.toLowerCase().includes('hospitalisation');

  const isPending =
    app.leave_status === 'submitted' || app.leave_status === 'pending';
  const isRejected = app.leave_status === 'rejected';
  const isApproved =
    app.leave_status === 'approved' || app.leave_status === 'auto_approved';
  const lt = (app.leave_type?.leave_type_name || '').toLowerCase();
  const isVerifiedSick = ((lt.includes('outpatient') && lt.includes('sick')) || lt.includes('hospitalisation')) && app.leave_status === 'approved';
  // Admins can cancel past-due leaves via the backend `admin_override` path
  // (with a mandatory cancellation reason). Verified sick leave remains
  // immutable for everyone — the backend rejects even admin cancel attempts.
  const isAdmin = typeof canAccessAdmin === 'function' && canAccessAdmin();
  const isCancellable =
    ['submitted', 'approved', 'auto_approved'].includes(app.leave_status) &&
    !isVerifiedSick &&
    (!app.is_past_due || isAdmin);

  const canHrCorrectWorkArrangement =
    typeof canAccessAdmin === 'function' &&
    canAccessAdmin() &&
    PHASE1_WORK_ARRANGEMENT_TYPES.includes(leaveTypeName.toLowerCase()) &&
    HR_CORRECTABLE_STATUSES.includes(app.leave_status);

  const handleHrWorkArrangementCorrect = async (e) => {
    e?.preventDefault?.();
    setHrFormError(null);
    if (hrReason.trim().length < 3) {
      setHrFormError('Please enter a reason (at least 3 characters).');
      return;
    }
    const startStr = formatDateForInput(hrStartDate);
    const endStr = formatDateForInput(hrEndDate);
    if (!startStr || !endStr) {
      setHrFormError('Start and end dates are required.');
      return;
    }
    setHrSubmitting(true);
    try {
      await staffAPI.hrCorrectWorkArrangement(applicationId, {
        leave_start_date: startStr,
        leave_end_date: endStr,
        leave_start_time_period: hrStartPeriod,
        leave_end_time_period: hrEndPeriod,
        amendment_reason: hrReason.trim(),
      });
      toast.success('Leave period updated.');
      setHrCorrectionOpen(false);
      await fetchDetail();
      onRefresh?.();
    } catch (err) {
      setHrFormError(formatApiError(err));
    } finally {
      setHrSubmitting(false);
    }
  };

  const headerNode = (
    <div className="modal-header">
      <div className="flex items-center gap-3 min-w-0">
        <FileText className="w-5 h-5 text-gray-600 flex-shrink-0" />
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="modal-title truncate">Leave Application Details</h3>
            {data && <StatusBadge status={app.leave_status} {...(isSickLeave && app.leave_status === 'approved' ? { label: 'Verified' } : {})} showIcon />}
          </div>
          {(profile.full_name || app.employee_name) && (
            <p className="text-sm text-gray-500 truncate">
              {profile.full_name || app.employee_name}
              {(profile.staff_id || app.staff_code) && (
                <>
                  <span className="mx-1.5 text-gray-400">·</span>
                  <span>{profile.staff_id || app.staff_code}</span>
                </>
              )}
            </p>
          )}
        </div>
      </div>
      <button onClick={onClose} className="modal-close-admin">
        <X className="w-5 h-5" />
      </button>
    </div>
  );

  const footerNode = (() => {
    if (data && extraFooterActions) return extraFooterActions;
    if (data && isPending && (onApprove || onReject) && !extraFooterActions) {
      return (
        <>
          {onReject && (
            <button
              onClick={() => {
                onReject(applicationId);
                onClose();
              }}
              className="btn btn-danger"
            >
              <XCircle className="w-4 h-4 mr-1.5" />
              Reject
            </button>
          )}
          {onCancel && isCancellable && (
            <button
              onClick={() => {
                onCancel(applicationId);
                onClose();
              }}
              className="btn btn-warning"
            >
              <Ban className="w-4 h-4 mr-1.5" />
              Cancel Leave
            </button>
          )}
          {onApprove && (
            <button
              type="button"
              disabled={isApproving}
              onClick={async () => {
                const result = await onApprove(applicationId);
                if (result !== false) onClose();
              }}
              className="btn btn-success"
            >
              {isApproving ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" aria-hidden="true" />
              ) : (
                <CheckCircle className="w-4 h-4 mr-1.5" aria-hidden="true" />
              )}
              {isApproving ? 'Approving…' : 'Approve'}
            </button>
          )}
        </>
      );
    }
    if (data && !isPending && !extraFooterActions) {
      return (
        <>
          {onCancel && isCancellable && (
            <button
              onClick={() => {
                onCancel(applicationId);
                onClose();
              }}
              className="btn btn-warning"
            >
              <Ban className="w-4 h-4 mr-1.5" />
              Cancel Leave
            </button>
          )}
          <button onClick={onClose} className="btn btn-secondary">
            Close
          </button>
        </>
      );
    }
    if (!data && !loading) {
      return (
        <button onClick={onClose} className="btn btn-secondary">
          Close
        </button>
      );
    }
    return null;
  })();

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      theme="admin"
      customHeader={headerNode}
      footer={footerNode}
      contentClassName="overflow-y-auto"
    >
            {loading && (
              <div className="flex items-center justify-center py-10">
                <LoadingSpinner size="lg" />
              </div>
            )}

            {error && (
              <div className="text-center py-8">
                <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-2" />
                <p className="text-red-600 font-medium">{error}</p>
                <button
                  onClick={fetchDetail}
                  className="mt-4 btn btn-admin-primary btn-sm"
                >
                  Retry
                </button>
              </div>
            )}

            {data && !loading && (
              <div className="space-y-3">
                {/* ── 0. Home Leave / Urgent Leave Process Stepper ── */}
                {(isHomeLeave || isUrgentLeave) && (() => {
                  const verif = app.flight_ticket_verification;
                  const ticketSubmitted = !!app.flight_details_submitted_at;
                  const ticketStatus = verif?.status;
                  const ticketApproved = ticketStatus === 'approved';
                  const ticketRejected = ticketStatus === 'rejected';

                  // Determine which step HR is currently on (read-only for HR)
                  let hrStep = 1;
                  if (app.leave_status === 'approved' && !ticketSubmitted) hrStep = 3;
                  else if (ticketApproved) hrStep = isHomeLeave ? 5 : 4;
                  else if (ticketSubmitted) hrStep = isHomeLeave ? 4 : 4;
                  else if (['submitted', 'pending'].includes(app.leave_status)) hrStep = 2;
                  else if (app.leave_status === 'approved') hrStep = 3;

                  const steps = isHomeLeave
                    ? [
                        { id: 1, label: 'Application', icon: FileText },
                        { id: 2, label: 'Approval', icon: CheckCircle },
                        { id: 3, label: 'Flight Ticket', icon: Upload },
                        { id: 4, label: 'HR Review', icon: Clock },
                        { id: 5, label: 'Complete', icon: CheckCircle },
                      ]
                    : [
                        { id: 1, label: 'Application', icon: FileText },
                        { id: 2, label: 'Approval', icon: CheckCircle },
                        { id: 3, label: 'Flight Details', icon: Upload },
                        { id: 4, label: 'Complete', icon: CheckCircle },
                      ];

                  return (
                    <section className="bg-red-50 border border-red-200 rounded-xl p-3.5">
                      <h5 className="text-xs font-semibold text-red-800 uppercase tracking-wide flex items-center gap-1.5 mb-3">
                        <Plane className="w-3.5 h-3.5" />
                        {isHomeLeave ? 'Home Leave Progress' : 'Urgent Leave Progress'}
                      </h5>
                      {/* Mini stepper */}
                      <div className="flex items-start">
                        {steps.map((step, idx) => {
                          const isComplete = step.id < hrStep;
                          const isCurrent = step.id === hrStep;
                          const Icon = step.icon;
                          return (
                            <React.Fragment key={step.id}>
                              <div className="flex flex-col items-center">
                                <div className={`
                                  w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold transition-all flex-shrink-0
                                  ${isComplete ? 'bg-red-700' : isCurrent ? 'bg-red-600 ring-2 ring-red-300' : 'bg-gray-300'}
                                `}>
                                  {isComplete ? <CheckCircle className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
                                </div>
                                <span className={`text-[10px] mt-1 text-center max-w-[52px] leading-tight ${isCurrent ? 'font-semibold text-red-800' : isComplete ? 'text-red-600' : 'text-gray-400'}`}>
                                  {step.label}
                                </span>
                              </div>
                              {idx < steps.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-1.5 mt-3.5 rounded ${step.id < hrStep ? 'bg-red-600' : 'bg-gray-200'}`} />
                              )}
                            </React.Fragment>
                          );
                        })}
                      </div>

                      {/* Rejection notice */}
                      {ticketRejected && verif?.rejection_reason && (
                        <div className="mt-2.5 bg-red-50 border border-red-200 rounded-lg p-2.5 text-xs text-red-800">
                          <span className="font-semibold">Ticket rejected:</span> {verif.rejection_reason}
                        </div>
                      )}

                      {/* HR action: verify — or attach the ticket the worker sent offline */}
                      {(isHomeLeave || isUrgentLeave) && app.leave_status === 'approved' && !ticketApproved && (
                        <button
                          type="button"
                          onClick={() => setShowFlightVerifyModal(true)}
                          className="mt-3 w-full btn btn-admin-primary btn-sm flex items-center justify-center gap-1.5 text-xs"
                        >
                          {ticketSubmitted ? (
                            <>
                              <CheckCircle className="w-3.5 h-3.5" />
                              {ticketRejected ? 'Re-verify Ticket' : 'Verify Flight Ticket'}
                            </>
                          ) : (
                            <>
                              <Upload className="w-3.5 h-3.5" />
                              Upload ticket on behalf of worker
                            </>
                          )}
                        </button>
                      )}
                    </section>
                  );
                })()}

                {/* ── 1. Employee Profile ── */}
                <section className="bg-gray-50 rounded-lg border border-gray-200 px-3.5 py-2.5">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-2.5 min-w-0 flex-1">
                      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <User className="w-4 h-4 text-red-600" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-gray-900 truncate">
                            {profile.full_name || app.employee_name || '—'}
                          </h4>
                          <span className="text-xs text-gray-500">
                            {profile.staff_id || app.staff_code || ''}
                            {profile.designation && ` · ${profile.designation}`}
                          </span>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-gray-500">
                          {profile.department && (
                            <span className="inline-flex items-center gap-1">
                              <Building className="w-3 h-3" />
                              {profile.department}
                            </span>
                          )}
                          {profile.trade_code &&
                            !isTradeRedundantWithDepartment(profile.department, profile.trade_code) && (
                            <TradeBadge code={profile.trade_code} size="xs" />
                          )}
                          {profile.reporting_officer_name && (
                            <span className="inline-flex items-center gap-1">
                              <Shield className="w-3 h-3" />
                              RO: {profile.reporting_officer_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {profile.employment_classification && (
                      <span className="flex-shrink-0">
                        <ClassificationBadge classification={profile.employment_classification} />
                      </span>
                    )}
                  </div>
                </section>

                {/* ── 2. Leave Details + Balance ── */}
                <section className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {/* Leave details — spans 2 cols */}
                  <div className="md:col-span-2 bg-white rounded-lg border border-gray-200 p-3.5">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Leave Details
                    </h5>
                    <div className="space-y-2">
                      {/* Leave Type + Emergency Badge */}
                      <div className="flex items-center gap-2 flex-wrap">
                        <LeaveTypeBadge leaveTypeName={displayLeaveTypeName} />
                        {app.leave_emergency && (
                          <InfoBadge color="red" label="Emergency" />
                        )}
                        {app.leave_is_bulk_deduction && (
                          <InfoBadge color="purple" label="Bulk Deduction" />
                        )}
                      </div>

                      {/* Date range */}
                      <div className="text-gray-700">
                        <LeavePeriodDisplay application={app} showIcon size="sm" />
                      </div>

                      {/* Duration */}
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <Clock className={`w-4 h-4 flex-shrink-0 ${isOILClaim && app.leave_status !== 'rejected' && app.leave_status !== 'cancelled' ? 'text-teal-500' : 'text-gray-400'}`} />
                        {isOILClaim ? (
                          <span className={getOilCreditDaysClassName(app.leave_status)}>
                            {formatOilCreditDaysLabel(getOilCreditedDays(app) ?? 0, app.leave_status)}
                          </span>
                        ) : (
                          <span className="font-semibold text-gray-900">
                            {(() => {
                              const days = getDisplayTotalDays(app);
                              return days
                                ? `${formatLeaveDays(days)} ${pluralizeDays(days)}`
                                : '—';
                            })()}
                          </span>
                        )}
                        {showConsumptionChips && (
                          <ConsumptionBreakdown
                            proposal={consumptionProposal}
                          />
                        )}
                      </div>

                      <LeaveExcludedDaysCallout
                        excludedDates={
                          hrCorrectionOpen
                            ? hrPreviewExcludedDates
                            : (app.excluded_dates || [])
                        }
                        leaveTypeName={displayLeaveTypeName}
                        audience="admin"
                      />

                      {consumptionProposal?.future_year_projection?.supported && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                          <div className="flex items-start gap-2">
                            <CalendarRange
                              className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"
                              aria-hidden="true"
                            />
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-blue-900">
                                {consumptionProposal.future_year_projection.balance_year} balance reservation
                              </p>
                              <p className="text-xs text-blue-800 mt-0.5">
                                {formatLeaveDays(consumptionProposal.future_year_projection.projected_carry_forward_days)} days projected carry-forward
                                {' · '}
                                {formatLeaveDays(consumptionProposal.future_year_projection.annual_leave_days_in_request)} AL days reserved
                                {' · '}
                                {formatLeaveDays(consumptionProposal.future_year_projection.projected_remaining_after_request)} days remaining after this request
                              </p>
                              {Number(consumptionProposal.future_year_projection.reserved_days_before_request || 0) > 0 && (
                                <p className="text-xs text-blue-700 mt-1">
                                  {formatLeaveDays(consumptionProposal.future_year_projection.reserved_days_before_request)} days were already reserved for other {consumptionProposal.future_year_projection.balance_year} leave.
                                </p>
                              )}
                              <p className="text-xs text-blue-700 mt-1">
                                This request does not reduce the employee&apos;s {consumptionProposal.future_year_projection.source_year} balance before rollover.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {consumptionProposal?.year_end_priority?.applied && (
                        <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
                          <div className="flex items-start gap-2">
                            <CalendarRange
                              className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0"
                              aria-hidden="true"
                            />
                            <div className="min-w-0">
                              <p className="text-xs font-semibold text-blue-900">
                                Year-end priority applied
                              </p>
                              <p className="text-xs text-blue-800 mt-0.5">
                                {formatLeaveDays(consumptionProposal.year_end_priority.at_risk_al_days)} days are projected above the {formatLeaveDays(consumptionProposal.year_end_priority.carry_forward_cap)}-day carry-forward limit. This request uses Annual Leave before {formatLeaveDays(consumptionProposal.year_end_priority.oil_days_valid_after_year_end)} days of OIL that remain valid into {consumptionProposal.year_end_priority.destination_year}.
                              </p>
                              <p className="text-xs text-blue-700 mt-1">
                                Excess Annual Leave expires on 31 December {consumptionProposal.year_end_priority.source_year}; later-expiring OIL is preserved.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Context-aware consumption warning banner */}
                      {(() => {
                        if (!consumptionProposal?.items?.length) return null;
                        const oilDays = consumptionProposal.items
                          .filter(i => i.leave_type === 'Off in Lieu')
                          .reduce((sum, item) => sum + (parseFloat(item.days) || 0), 0);
                        const ulItem = consumptionProposal.items.find(
                          i => i.leave_type === 'Unpaid Leave' && parseFloat(i.days) > 0
                        );
                        const hlItem = consumptionProposal.items.find(
                          i => i.leave_type === 'Home Leave' && parseFloat(i.days) > 0
                        );
                        const isPreview = consumptionProposal.is_preview;

                        if (ulItem) {
                          const ulDays = parseFloat(ulItem.days);
                          return (
                            <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span>
                                <span className="font-semibold">{formatLeaveDays(ulDays)} {pluralizeDays(ulDays)} will be Unpaid Leave</span>
                                {' '}— this may reduce the employee's salary for the affected pay period.
                              </span>
                            </div>
                          );
                        }
                        if (hlItem) {
                          const alDays = consumptionProposal.items
                            .filter(i => i.leave_type === 'Annual Leave')
                            .reduce((sum, item) => sum + (parseFloat(item.days) || 0), 0);
                          const hlDays = parseFloat(hlItem.days);
                          return (
                            <HomeLeaveConsumptionNote
                              alDays={alDays}
                              hlDays={hlDays}
                              audience="admin"
                            />
                          );
                        }
                        if (oilDays > 0 && leaveTypeName !== 'Off in Lieu') {
                          const alDays = consumptionProposal.items
                            .filter(i => i.leave_type === 'Annual Leave')
                            .reduce((sum, item) => sum + (parseFloat(item.days) || 0), 0);
                          return (
                            <div className="flex items-start gap-2 text-xs text-teal-700 bg-teal-50 rounded-lg px-3 py-2">
                              <TrendingUp className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                              <span>
                                <span className="font-semibold">{formatLeaveDays(oilDays)} {pluralizeDays(oilDays)}</span>
                                {' '}deducted from the earliest-expiring Off in Lieu credits.
                                {alDays > 0 && (
                                  <>{' '}Remaining <span className="font-semibold">{formatLeaveDays(alDays)} {pluralizeDays(alDays)}</span> from Annual Leave.</>
                                )}
                                {isPreview && <span className="ml-1 opacity-70">(est.)</span>}
                              </span>
                            </div>
                          );
                        }
                        return null;
                      })()}

                      {/* Public holidays flag */}
                      {app.includes_public_holidays && publicHolidays.length > 0 && (
                        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
                          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                          <span>
                            Includes {publicHolidays.length} public holiday{publicHolidays.length > 1 ? 's' : ''}
                            {publicHolidays.length <= 3 && (
                              <>: {publicHolidays.map((h) => h.name || h.holiday_name || formatDate(h.date || h.holiday_date)).join(', ')}</>
                            )}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Balance snapshot — 1 col */}
                  <div className="bg-white rounded-lg border border-gray-200 p-3.5">
                    {/* OIL balance — shown when OIL is the consumed balance type */}
                    {hasOilConsumption && oilBalance && (
                      <>
                        <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          OIL Balance ({oilBalance.year || new Date().getFullYear()})
                        </h5>
                        <div className="space-y-1.5 mb-3">
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-500">Credited</span>
                            <span className="text-sm font-semibold text-gray-900">{formatLeaveDays(oilBalance.entitled_days)} days</span>
                          </div>
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-500">Used</span>
                            <span className="text-sm font-medium text-orange-600">{formatLeaveDays(oilBalance.used_days)}</span>
                          </div>
                          <div className="border-t border-gray-200 pt-1.5 flex items-baseline justify-between">
                            <span className="text-xs font-semibold text-gray-700">Remaining</span>
                            <span className={`text-sm font-bold ${oilBalance.remaining_days <= 0 ? 'text-red-600' : 'text-teal-600'}`}>
                              {formatLeaveDays(oilBalance.remaining_days)}
                            </span>
                          </div>
                          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-all ${oilBalance.remaining_days <= 0 ? 'bg-red-500' : 'bg-teal-500'}`}
                              style={{
                                width: `${Math.min(100, Math.max(0,
                                  ((oilBalance.entitled_days - oilBalance.used_days) / Math.max(oilBalance.entitled_days, 1)) * 100
                                ))}%`,
                              }}
                            />
                          </div>
                        </div>
                        <div className="border-t border-gray-100 pt-3" />
                      </>
                    )}
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      {balance
                        ? `${balance.leave_type_name || 'Balance'} (${balance.year || new Date().getFullYear()})`
                        : overseasLeave
                        ? `Overseas Leave Cap (${overseasLeave.year})`
                        : 'Balance'}
                    </h5>
                    {balance ? (
                      <div className="space-y-1.5">
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-gray-500">Entitled</span>
                          <span className="text-sm font-semibold text-gray-900">{formatLeaveDays(balance.entitled_days)} days</span>
                        </div>
                        {balance.carried_forward_days > 0 && (
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-500">Brought Forward</span>
                            <span className="text-sm font-medium text-blue-700">+{formatLeaveDays(balance.carried_forward_days)}</span>
                          </div>
                        )}
                        {balance.accrued_days > 0 && balance.accrued_days !== balance.entitled_days && (
                          <div className="flex items-baseline justify-between">
                            <span className="text-xs text-gray-500">Accrued</span>
                            <span className="text-sm font-medium text-indigo-700">{formatLeaveDays(balance.accrued_days)}</span>
                          </div>
                        )}
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-gray-500">Used</span>
                          <span className="text-sm font-medium text-orange-600">{formatLeaveDays(balance.used_days)}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-1.5 flex items-baseline justify-between">
                          <span className="text-xs font-semibold text-gray-700">Remaining</span>
                          <span className={`text-sm font-bold ${balance.remaining_days <= 0 ? 'text-red-600' : balance.remaining_days <= 3 ? 'text-amber-600' : 'text-green-600'}`}>
                            {formatLeaveDays(balance.remaining_days)}
                          </span>
                        </div>
                        {/* Mini progress bar */}
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              balance.remaining_days <= 0
                                ? 'bg-red-500'
                                : balance.remaining_days <= 3
                                ? 'bg-amber-500'
                                : 'bg-green-500'
                            }`}
                            style={{
                              width: `${Math.min(
                                100,
                                Math.max(
                                  0,
                                  ((balance.total_available_days - balance.used_days) /
                                    Math.max(balance.total_available_days, 1)) *
                                    100
                                )
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    ) : overseasLeave ? (
                      <div className="space-y-1.5">
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-gray-500">Annual Cap</span>
                          <span className="text-sm font-semibold text-gray-900">{overseasLeave.cap_days} calendar days</span>
                        </div>
                        <div className="flex items-baseline justify-between">
                          <span className="text-xs text-gray-500">Used</span>
                          <span className="text-sm font-medium text-orange-600">{overseasLeave.used_days}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-1.5 flex items-baseline justify-between">
                          <span className="text-xs font-semibold text-gray-700">Remaining</span>
                          <span className={`text-sm font-bold ${overseasLeave.remaining_days <= 0 ? 'text-red-600' : overseasLeave.remaining_days <= 7 ? 'text-amber-600' : 'text-green-600'}`}>
                            {overseasLeave.remaining_days}
                          </span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              overseasLeave.remaining_days <= 0
                                ? 'bg-red-500'
                                : overseasLeave.remaining_days <= 7
                                ? 'bg-amber-500'
                                : 'bg-green-500'
                            }`}
                            style={{
                              width: `${Math.min(100, Math.max(0,
                                ((overseasLeave.cap_days - overseasLeave.used_days) / Math.max(overseasLeave.cap_days, 1)) * 100
                              ))}%`,
                            }}
                          />
                        </div>
                        <p className="text-[10px] text-gray-400 mt-1">
                          Combined Home Leave + Urgent Leave per calendar year
                        </p>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-400 italic">
                        {(isWFH || isExternalActivity || isOnCourse)
                          ? 'Not applicable — no entitlement for this leave type.'
                          : consumptionProposal?.future_year_projection?.supported
                          ? `The ${consumptionProposal.future_year_projection.balance_year} balance will be created at rollover; the approved projection is shown above.`
                          : 'No balance record found for this leave type.'}
                      </p>
                    )}
                  </div>
                </section>

                {/* HR Phase 1: correct dates / half-days for work-arrangement types (admin theme — design-system-reference.md) */}
                {data && !loading && canHrCorrectWorkArrangement && (
                  <section
                    className="rounded-xl border border-red-200 bg-red-50/90 p-3.5"
                    aria-label="HR correction — work arrangement period"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                      <div className="min-w-0">
                        <h5 className="text-xs font-semibold text-red-800 uppercase tracking-wide flex items-center gap-1.5">
                          <CalendarRange className="w-3.5 h-3.5 flex-shrink-0" />
                          HR: correct period
                        </h5>
                        <p className="text-xs text-red-900/80 mt-1">
                          External Activity, On Course, and Work From Home only. Working days and overlaps are validated on save.
                          Does not change leave balances.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setHrCorrectionOpen((v) => !v);
                          setHrFormError(null);
                        }}
                        className="btn btn-admin-outline btn-sm flex-shrink-0 self-start sm:self-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                      >
                        {hrCorrectionOpen ? 'Hide form' : 'Correct dates & half-days'}
                      </button>
                    </div>

                    {hrCorrectionOpen && (
                      <form
                        className="mt-3 space-y-3 pt-3 border-t border-red-200"
                        onSubmit={handleHrWorkArrangementCorrect}
                      >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label htmlFor="hr-correct-start" className="block text-xs font-medium text-gray-700 mb-1">
                              Start date
                              <span className="text-gray-500 font-normal"> · first day</span>
                            </label>
                            {/* Single horizontal row: date + AM/PM (DS: inline with segmented control) */}
                            <div className="flex flex-nowrap items-center gap-2 min-w-0">
                              <div className="flex-1 min-w-0">
                                <DatePickerField
                                  id="hr-correct-start"
                                  theme="admin"
                                  showIcon={false}
                                  selected={hrStartDate}
                                  onChange={(d) => setHrStartDate(d)}
                                  className="input-admin w-full"
                                  placeholderText="Select start"
                                  popperPlacement="bottom-start"
                                />
                              </div>
                              <LeaveHalfDaySegmentedControl
                                theme="admin"
                                value={hrStartPeriod}
                                onChange={setHrStartPeriod}
                                ariaLabel="First day: morning or afternoon"
                              />
                            </div>
                          </div>
                          <div>
                            <label htmlFor="hr-correct-end" className="block text-xs font-medium text-gray-700 mb-1">
                              End date
                              <span className="text-gray-500 font-normal"> · last day</span>
                            </label>
                            <div className="flex flex-nowrap items-center gap-2 min-w-0">
                              <div className="flex-1 min-w-0">
                                <DatePickerField
                                  id="hr-correct-end"
                                  theme="admin"
                                  showIcon={false}
                                  selected={hrEndDate}
                                  onChange={(d) => setHrEndDate(d)}
                                  className="input-admin w-full"
                                  placeholderText="Select end"
                                  minDate={hrStartDate || undefined}
                                  popperPlacement="bottom-start"
                                />
                              </div>
                              <LeaveHalfDaySegmentedControl
                                theme="admin"
                                value={hrEndPeriod}
                                onChange={setHrEndPeriod}
                                ariaLabel="Last day: morning or afternoon"
                              />
                            </div>
                          </div>
                        </div>

                        <div className="rounded-lg bg-white/80 border border-red-100 px-3 py-2 text-xs text-gray-700">
                          <span className="font-semibold text-gray-800">Preview (working duration):</span>{' '}
                          {hrPreviewLoading ? (
                            <span className="text-gray-500">Calculating…</span>
                          ) : hrPreviewDays != null ? (
                            <span>{formatLeaveDays(hrPreviewDays)} {pluralizeDays(hrPreviewDays)}</span>
                          ) : (
                            <span className="text-gray-500">—</span>
                          )}
                          <span className="text-gray-500 block mt-0.5">
                            Final value is computed on the server (schedule + public holidays).
                          </span>
                        </div>

                        <div>
                          <label htmlFor="hr-correct-reason" className="block text-xs font-medium text-gray-700 mb-1">
                            Reason for correction <span className="text-red-600">*</span>
                          </label>
                          <textarea
                            id="hr-correct-reason"
                            rows={3}
                            value={hrReason}
                            onChange={(ev) => setHrReason(ev.target.value)}
                            className="textarea-admin w-full text-sm"
                            placeholder="e.g. Employee requested PM on last day; half-day was entered incorrectly."
                            autoComplete="off"
                          />
                        </div>

                        {hrFormError && (
                          <div
                            className="flex items-start gap-2 text-xs text-red-800 bg-red-100 border border-red-200 rounded-lg px-3 py-2"
                            role="alert"
                          >
                            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                            <span>{hrFormError}</span>
                          </div>
                        )}

                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => {
                              setHrCorrectionOpen(false);
                              setHrFormError(null);
                            }}
                            className="btn btn-admin-outline w-full sm:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                            disabled={hrSubmitting}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-admin-primary w-full sm:w-auto focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                            disabled={hrSubmitting}
                          >
                            {hrSubmitting ? 'Saving…' : 'Save correction'}
                          </button>
                        </div>
                      </form>
                    )}
                  </section>
                )}

                {/* ── 3. Reason / Context (adaptive) ── */}
                <section className="bg-white rounded-lg border border-gray-200 p-3.5">
                  <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                    {isOILClaim ? 'Off in Lieu Claim Details' :
                     isExternalActivity ? 'External Activity Details' :
                     isOnCourse ? 'On Course Details' :
                     isWFH ? 'Work From Home Details' :
                     isHomeLeave || isUrgentLeave ? `${leaveTypeName} Details` :
                     'Reason'}
                  </h5>

                  {/* Generic reason (shown for most types — HL/UGL / EA / WFH / On Course render their own blocks) */}
                  {!isExternalActivity && !isWFH && !isOILClaim && !isHomeLeave && !isUrgentLeave && !isOnCourse && (
                    <p className="text-sm text-gray-800 mb-3">
                      {app.leave_reason || <span className="text-gray-400 italic">No reason provided</span>}
                    </p>
                  )}

                  {/* WFH reason */}
                  {isWFH && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-800">
                        {app.wfh_reason
                          ? app.wfh_reason.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
                          : app.leave_reason || 'No reason provided'}
                      </p>
                      {app.wfh_other_reason && (
                        <p className="text-sm text-gray-600 italic">{app.wfh_other_reason}</p>
                      )}
                    </div>
                  )}

                  {/* External Activity details */}
                  {isExternalActivity && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                      {app.activity_type && (
                        <div>
                          <span className="text-gray-500">Activity Type:</span>{' '}
                          <span className="text-gray-900 font-medium">{app.activity_type}</span>
                        </div>
                      )}
                      {app.activity_location && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5 text-gray-400" />
                          <span className="text-gray-900">{app.activity_location}</span>
                        </div>
                      )}
                      {app.activity_project_code && (
                        <div>
                          <span className="text-gray-500">Project:</span>{' '}
                          {app.activity_project_name ? (
                            <>
                              <span className="text-gray-900 font-medium">{app.activity_project_name}</span>
                              {' '}
                              <span className="font-mono text-gray-500">({app.activity_project_code})</span>
                            </>
                          ) : (
                            <span className="font-mono text-gray-900">{app.activity_project_code}</span>
                          )}
                        </div>
                      )}
                      {app.business_justification && (
                        <div className="md:col-span-2">
                          <span className="text-gray-500">Justification:</span>{' '}
                          <span className="text-gray-900">{app.business_justification}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* On Course — structured fields with legacy leave_reason parsing */}
                  {isOnCourse && (() => {
                    const info = getCourseInfo(app);
                    if (!info) {
                      return <p className="text-sm text-gray-400 italic">No course details provided</p>;
                    }
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        {info.courseName && (
                          <div className="flex items-start gap-1 md:col-span-2">
                            <GraduationCap className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="text-gray-500">Course / Training:</span>{' '}
                              <span className="text-gray-900 font-medium">{info.courseName}</span>
                            </div>
                          </div>
                        )}
                        {info.courseLocation && (
                          <div className="flex items-start gap-1 md:col-span-2">
                            <MapPin className="w-3.5 h-3.5 text-gray-400 mt-0.5 flex-shrink-0" />
                            <div>
                              <span className="text-gray-500">Location:</span>{' '}
                              <span className="text-gray-900">{info.courseLocation}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* OIL Claim details */}
                  {isOILClaim && (
                    <div className="space-y-3">
                      {app.oil_auto_credited && app.leave_reason && (
                        <div className="flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-sm">
                          <CalendarRange className="w-4 h-4 text-indigo-500 flex-shrink-0" />
                          <span className="text-indigo-800">{app.leave_reason}</span>
                          <span className="ml-auto text-xs text-indigo-500 font-medium whitespace-nowrap">System Credit</span>
                        </div>
                      )}
                      {!app.oil_auto_credited && app.oil_work_description && (
                        <p className="text-sm text-gray-800">{app.oil_work_description}</p>
                      )}
                      {app.oil_project_code && (
                        <p className="text-sm text-gray-600">
                          Project:{' '}
                          {app.oil_project_name ? (
                            <>
                              <span className="text-gray-900 font-medium">{app.oil_project_name}</span>
                              {' '}
                              <span className="font-mono text-gray-500">({app.oil_project_code})</span>
                            </>
                          ) : (
                            <span className="font-mono">{app.oil_project_code}</span>
                          )}
                        </p>
                      )}
                      {app.oil_dates && app.oil_dates.length > 0 && (
                        <OilClaimWorkDates oilDates={app.oil_dates} expiryDays={OIL_EXPIRY_DAYS} />
                      )}
                      {!app.oil_dates?.length && app.leave_reason && (
                        <p className="text-sm text-gray-800">{app.leave_reason}</p>
                      )}
                    </div>
                  )}

                  {/* OIL Usage note */}
                  {isOILUsage && (
                    <div className="bg-blue-50 rounded-lg p-3 text-sm text-blue-800">
                      Employee is using accrued Off in Lieu balance.
                      This will deduct <strong>{formatLeaveDays(app.leave_total_days)} day{parseFloat(app.leave_total_days) > 1 ? 's' : ''}</strong> from their OIL balance upon approval.
                    </div>
                  )}

                  {/* Home Leave / Urgent Leave — flight details */}
                  {(isHomeLeave || isUrgentLeave) && (
                    <div className="space-y-2 mt-2">
                      {app.leave_reason && (
                        <p className="text-sm text-gray-800 mb-3">{app.leave_reason}</p>
                      )}
                      {(app.flight_departure_date || app.flight_return_date) && (() => {
                        const verif = app.flight_ticket_verification;
                        const ticketApproved = verif?.status === 'approved';
                        const ticketPending = verif && !ticketApproved && verif.status !== 'rejected';
                        return (
                          <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm">
                            <div className="flex items-center justify-between mb-2">
                              <h6 className="font-medium text-sky-800">Flight Details</h6>
                              {ticketApproved && verif?.document_approval_id && (
                                <button
                                  onClick={() => setShowFlightVerifyModal(true)}
                                  className="inline-flex items-center gap-1 text-xs text-sky-700 hover:text-sky-900 font-medium transition-colors"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  View Ticket
                                </button>
                              )}
                              {ticketPending && verif?.document_approval_id && (
                                <button
                                  onClick={() => setShowFlightVerifyModal(true)}
                                  className="inline-flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium transition-colors"
                                >
                                  <CheckCircle className="w-3.5 h-3.5" />
                                  Verify Ticket
                                </button>
                              )}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div>
                                <span className="text-sky-600 text-xs">Departure</span>
                                <p className="font-medium text-sky-900">
                                  {app.flight_departure_date ? formatDate(app.flight_departure_date) : '—'}
                                </p>
                              </div>
                              <div>
                                <span className="text-sky-600 text-xs">Return</span>
                                <p className="font-medium text-sky-900">
                                  {app.flight_return_date ? formatDate(app.flight_return_date) : isUrgentLeave ? 'One-way' : '—'}
                                </p>
                              </div>
                            </div>
                            {verif && (
                              <div className="mt-2 pt-2 border-t border-sky-200 flex items-center gap-1.5">
                                <span className="text-sky-600 text-xs">Ticket Status</span>
                                {ticketApproved && (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                                    <CheckCircle className="w-3 h-3" /> Verified
                                  </span>
                                )}
                                {ticketPending && (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                                    <Clock className="w-3 h-3" /> Pending Verification
                                  </span>
                                )}
                                {verif.status === 'rejected' && (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                                    <XCircle className="w-3 h-3" /> Rejected
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </section>

                <OverseasTravelDeclarationSummary application={app} audience="hr" />

                {/* ── Compassionate Leave — supporting document (advisory) ── */}
                {isCompassionate && app.compassionate_document && (() => {
                  const doc = app.compassionate_document;
                  const ds = doc.doc_status;
                  const verified = ds === 'verified';
                  const pending = ds === 'pending_verification';
                  const rejected = ds === 'rejected';
                  const awaiting = ds === 'awaiting_upload';
                  return (
                    <section className="bg-white rounded-lg border border-gray-200 p-3.5">
                      <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                        <FileText className="w-4 h-4" />
                        Supporting Document
                      </h5>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-gray-800">Death Certificate</span>
                        {verified && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded-full px-2 py-0.5">
                            <CheckCircle className="w-3 h-3" /> Verified
                          </span>
                        )}
                        {pending && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                            <Clock className="w-3 h-3" /> Pending Verification
                          </span>
                        )}
                        {awaiting && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                            <Upload className="w-3 h-3" /> Awaiting Upload
                          </span>
                        )}
                        {rejected && (
                          <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded-full px-2 py-0.5">
                            <XCircle className="w-3 h-3" /> Rejected
                          </span>
                        )}
                      </div>
                      {(awaiting || rejected) && doc.due_date && (
                        <p className={`text-xs mt-2 ${doc.is_overdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                          {doc.is_overdue ? 'Overdue — ' : ''}Due by {formatDate(doc.due_date)} (within 30 days of leave start)
                        </p>
                      )}
                      {rejected && doc.rejection_reason && (
                        <p className="text-xs text-red-600 mt-1">Reason: {doc.rejection_reason}</p>
                      )}
                      {verified && doc.approved_by_name && (
                        <p className="text-xs text-gray-500 mt-2">
                          Verified by {doc.approved_by_name}
                          {doc.approved_at ? ` on ${formatDate(doc.approved_at)}` : ''}
                        </p>
                      )}
                      <p className="text-[11px] text-gray-400 mt-2 leading-snug">
                        HR uploads the death certificate on the employee's behalf from Employees → Documents.
                        This is advisory and does not block approval.
                      </p>
                    </section>
                  );
                })()}

                {/* ── 4. Coverage ── */}
                {(app.coverage_staff_name || app.leave_coverage_notes) && (
                  <section className="bg-white rounded-lg border border-gray-200 p-3.5">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                      Coverage Arrangements
                    </h5>
                    <div className="flex items-center gap-2 text-sm text-gray-800">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{app.coverage_staff_name || 'Not specified'}</span>
                    </div>
                    {app.leave_coverage_notes && (
                      <p className="text-sm text-gray-600 mt-2 pl-6">{app.leave_coverage_notes}</p>
                    )}
                  </section>
                )}

                {/* ── 5. Approval Timeline ── */}
                <ApprovalTimeline leave={app} />

                {/* ── 6. Government Reimbursement ── */}
                {app.reimbursement_eligible && (
                  <section className="bg-green-50 rounded-lg border border-green-200 p-3.5">
                    <h5 className="text-xs font-semibold text-green-800 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">
                      <Award className="w-4 h-4" />
                      Government Reimbursement
                    </h5>
                    <div className="flex items-center gap-4 text-sm text-green-800">
                      <span className="font-medium">Eligible</span>
                      {app.reimbursement_caps && (
                        <>
                          <span>Cap/week: {app.reimbursement_caps.cap_per_week || '—'}</span>
                          <span>Weeks: {app.reimbursement_caps.cap_period_weeks || '—'}</span>
                        </>
                      )}
                    </div>
                  </section>
                )}

                {/* ── 7. Related Applications ── */}
                {(relatedApps.length > 0 || parentApp) && (
                  <section className="bg-white rounded-lg border border-gray-200 p-3.5">
                    <h5 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                      Related Applications
                    </h5>

                    {/* Parent link — shown when this is a consumption child or an extension */}
                    {parentApp && (
                      <div className="mb-2 text-sm text-gray-700 bg-gray-50 rounded-lg p-2.5 flex items-center gap-2">
                        <ChevronRight className="w-3.5 h-3.5 text-gray-400 rotate-180 flex-shrink-0" />
                        <span className="text-gray-500">Parent:</span>{' '}
                        <span className="font-medium">{parentApp.leave_type_name}</span>
                        {parentApp.is_extension && (
                          <InfoBadge color="amber" label="Extension" />
                        )}
                        <span className="text-gray-400">—</span>
                        <span>{formatLeaveDays(parentApp.leave_total_days)} days ({parentApp.leave_status})</span>
                      </div>
                    )}

                    {/* Child rows — separated into extensions vs consumption records */}
                    {relatedApps.length > 0 && (() => {
                      const extensionChildren = relatedApps.filter(c => c.is_extension);
                      const consumptionChildren = relatedApps.filter(c => !c.is_extension);
                      return (
                        <div className="space-y-2">
                          {/* Consumption breakdown (AL + HL deduction records) */}
                          {consumptionChildren.map((child) => (
                            <div
                              key={child.id}
                              className="flex items-center justify-between text-sm bg-gray-50 rounded-lg p-3"
                            >
                              <div className="flex items-center gap-2">
                                <LeaveTypeBadge leaveTypeName={child.leave_type_name} size="xs" />
                                <span className="text-gray-500">
                                  {child.leave_start_date ? formatDate(child.leave_start_date) : ''}{' '}
                                  {child.leave_end_date && child.leave_start_date !== child.leave_end_date
                                    ? `→ ${formatDate(child.leave_end_date)}`
                                    : ''}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">{formatLeaveDays(child.leave_total_days)} days</span>
                                <StatusBadge status={child.leave_status} />
                              </div>
                            </div>
                          ))}

                          {/* Extension records — visually distinct with amber styling */}
                          {extensionChildren.length > 0 && (
                            <>
                              {consumptionChildren.length > 0 && (
                                <div className="border-t border-gray-100 pt-2 mt-2">
                                  <div className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1.5">Extensions</div>
                                </div>
                              )}
                              {extensionChildren.map((child) => (
                                <div
                                  key={child.id}
                                  className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-1"
                                >
                                  <div className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-2">
                                      <ChevronRight className="w-3.5 h-3.5 text-amber-500" />
                                      <span className="font-medium text-gray-900">
                                        {formatDate(child.leave_start_date)}
                                        {child.leave_end_date && child.leave_start_date !== child.leave_end_date
                                          ? ` → ${formatDate(child.leave_end_date)}`
                                          : ''}
                                      </span>
                                      <InfoBadge color="amber" label="Extension" />
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="font-medium text-sm">
                                        {formatLeaveDays(child.leave_total_days)} days
                                      </span>
                                      <StatusBadge status={child.leave_status} />
                                    </div>
                                  </div>
                                  {child.extension_reason && (
                                    <div className="text-xs text-gray-600 pl-5">
                                      <span className="text-gray-400">Reason: </span>
                                      {child.extension_reason}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      );
                    })()}
                  </section>
                )}
              </div>
            )}
    </Modal>
      {/* Flight Ticket Verification Modal */}
      {showFlightVerifyModal && (isHomeLeave || isUrgentLeave) && (
        <FlightTicketVerificationModal
          isOpen={showFlightVerifyModal}
          onClose={() => setShowFlightVerifyModal(false)}
          documentApprovalId={app.flight_ticket_verification?.document_approval_id || null}
          leaveApplication={app}
          onVerificationComplete={(result) => {
            setShowFlightVerifyModal(false);
            fetchDetail(); // Refresh the detail to show updated status
            onRefresh?.();
          }}
        />
      )}
    </>
  );
};

export default LeaveApplicationDetailModal;
